import React from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { deleteContact } from 'api/contacts/single';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DeleteContactProps {
    children?: React.ReactNode;
    onSuccess?: () => void;
}

const DeleteContact = ({ onSuccess, children }: DeleteContactProps) => {
    const { key, forceRender } = useForceRender();
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { data: contact } = useCurrentContact();
    const toast = useToast();

    const { mutate: deleteSingleContact, isPending } = useMutation({
        mutationFn: () => deleteContact(accountId, contact.id),
        onSuccess: () => {
            toast.success('Contact was deleted successfully');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            forceRender();
            onSuccess?.();
            history.push(`/accounts/${accountId}/contacts`);
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <AlertDialog.Root key={key} type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faTrash} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Title>Delete contact record?</AlertDialog.Title>
                    <AlertDialog.Body>
                        {isPending && <Loader />}
                        <AlertDialog.Description>
                            Deleting a contact is an irreversible action and cannot be undone.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <Button
                            className="flex-1"
                            variant="outline"
                            color="neutral"
                            onClick={forceRender}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            className="flex-1"
                            color="error"
                            onClick={() => deleteSingleContact()}
                        >
                            Delete
                        </Button>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteContactTrigger = AlertDialog.Trigger;

const Root = DeleteContact;
const Trigger = DeleteContactTrigger;

export {
    Root,
    Trigger,
    //
    DeleteContact,
    DeleteContactTrigger,
};
